import React, { useEffect, useState, useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart, CategoryScale, LinearScale, Title, BarElement, Tooltip } from "chart.js";
import { Card, Row, Spinner } from "react-bootstrap";
import { fetchReportInteractions } from "../../../services/httpClient";

// Register the required Chart.js scales
Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

interface ShelterInfractionsBarChartProps {
  isActive?: boolean;
}

export const ShelterInfractionsBarChart = ({ isActive = false }: ShelterInfractionsBarChartProps) => {
  const [current, setCurrent] = useState<any>();
  const chartRef = useRef<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      const start = new Date();
      start.setDate(start.getDate() - 7);
      await getInteractions(start.toISOString().slice(0, 10), new Date().toISOString().slice(0, 10));
    };

    fetchData();
  }, []);

  // Trigger a resize when the tab becomes active
  useEffect(() => {
    if (!isActive || !chartRef.current) return;
    const inst = chartRef.current?.chart || chartRef.current;
    if (typeof inst.resize === "function") inst.resize();
  }, [isActive]);

  const getInteractions = async (start: string, end: string) => {
    await fetchReportInteractions(start, end)
      .then((res: any) => {
        var data = res[0].data;
        var infractions = data.infraction_type_counts;
        infractions = infractions?.map((infraction: any) => {
          if (infraction?.created_at) {
            const utcDate = new Date(infraction?.created_at + " UTC");
            const pad = (n: any) => n.toString().padStart(2, "0");
            const year = utcDate.getFullYear();
            const month = pad(utcDate.getMonth() + 1);
            const day = pad(utcDate.getDate());
            const hours = pad(utcDate.getHours());
            const minutes = pad(utcDate.getMinutes());
            const seconds = pad(utcDate.getSeconds());
            const localTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
            return { ...infraction, created_at_local: localTime };
          } else {
            return infraction;
          }
        });

        //use interactions to get the bar data
        var barData = infractions?.reduce((groups: any, item: any) => {
          const { infraction_type } = item;

          if (!groups[infraction_type]) {
            groups[infraction_type] = {
              infraction_type,
              count: 0,
            };
          }

          groups[infraction_type].count++;

          return groups;
        }, {});

        barData = barData ?? {};
        setCurrent(Object.values(barData));
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  if (!current)
    return (
      <Card className="p-3">
        <Card.Body>
          <Row className="text-center">
            <text className="mt-2 mb-2">
              <Spinner animation="border" style={{ color: "#F6893D" }} />
            </text>
          </Row>
        </Card.Body>
      </Card>
    );

  const chartData = {
    labels: current?.map((item: any) => item.infraction_type),
    datasets: [
      {
        label: "Infractions",
        data: current?.map((item: any) => item.count),
        backgroundColor: ["#5D82A0"],
      },
    ],
  };

  const options = {
    indexAxis: "y" as const,
    scales: {
      y: {
        beginAtZero: true,
        max: Math.max(...current.map((item: any) => item.count)) + 50,
        ticks: {
          font: {
            size: 10,
          },
        },
      },
    },
    plugins: {
      title: {
        display: true,
        text: "Last 7 Days Infractions",
      },
    },
    elements: {
      bar: {
        borderWidth: 1,
        borderRadius: 4,
        barPercentage: 0.8,
        categoryPercentage: 0.9,
      },
    },
  };

  return (
    <Card className="p-3">
      <Bar ref={chartRef} data={chartData} options={options} />
    </Card>
  );
};
